import { defineStore } from 'pinia';
import { TotpSecretGenerator, TotpStatus } from '@schema';
import api from '@/common/utils/api';
import useThemeStore from '@/common/store/pinia/theme';
import isSelfService from '@/common/utils/isSelfService';
import SelfServiceAssets from '@/self-service/config/themes';
import HciAssets from '@/hci/config/themes';
import { OPENSTACK_AUTH_RECEIPT_HEADER } from '@/common/constants/auth.constants';

const TOTP_API_URL = '/api/v2/accounts/totp';

const defaultAssets = isSelfService() ? SelfServiceAssets[APP_THEME] : HciAssets[APP_THEME];

export interface TotpState {
  secret: string;
  status: TotpStatus.status | null;
  loading: boolean;
  pending: boolean;
  error: string;
}

const getInitialState = (): TotpState => ({
  secret: '',
  status: null,
  loading: false,
  pending: false,
  error: ''
});

export const useTotpStore = defineStore('totp', {
  state: () => getInitialState(),
  getters: {
    isTotpEnabled: state => state.status === TotpStatus.status.ENABLED,
    isTotpEnforced: state => state.status === TotpStatus.status.ENFORCED,
    totpStatus: state => state.status,
    totpSecret: state => state.secret,
    isLoading: state => state.loading,
    isPending: state => state.pending,
    totpError: state => state.error,
    otpAuthUrl: state => (username: string) => {
      const themeStore = useThemeStore();
      const issuer = themeStore.themeData?.productTitle || defaultAssets.productName;
      return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(username)}?secret=${state.secret}&issuer=${encodeURIComponent(issuer)}`;
    }
  },
  actions: {
    async fetchStatus() {
      this.loading = true;
      this.error = '';
      try {
        const response = await api.get<TotpStatus>(TOTP_API_URL);
        this.status = response.data.status as TotpStatus.status;
        return response;
      } catch (err) {
        this.error = (err as Error).message;
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async generateSecret() {
      this.loading = true;
      this.error = '';
      try {
        const response = await api.get<TotpSecretGenerator>(`${TOTP_API_URL}/generator`);
        this.secret = response.data.secret;
        return response;
      } catch (err) {
        this.error = (err as Error).message;
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async enableTotp(passcode: string, receipt?: string) {
      this.pending = true;
      this.error = '';

      try {
        const headers = receipt ? { [OPENSTACK_AUTH_RECEIPT_HEADER]: receipt } : undefined;
        const response = await api.post<TotpStatus>(
          TOTP_API_URL,
          { secret: this.secret, passcode },
          { headers }
        );

        const responseStatus = (response as any)?.data?.status as TotpStatus.status | undefined;

        if (responseStatus) {
          this.status = responseStatus;
        } else {
          await this.fetchStatus();
        }

        return response;
      } catch (err) {
        this.error = (err as Error).message;
        throw err;
      } finally {
        this.pending = false;
      }
    },
    async disableTotp(passcode?: string) {
      this.pending = true;
      this.error = '';
      try {
        await api.delete(`${TOTP_API_URL}`, passcode ? { passcode } : undefined);
        this.secret = '';
        return this.fetchStatus();
      } catch (err) {
        this.error = (err as Error).message;
        throw err;
      } finally {
        this.pending = false;
      }
    },
    resetTotp(userId: string, passCode?: string) {
      this.pending = true;
      this.error = '';
      return api.delete(`${TOTP_API_URL}`, { user_id: userId, passcode: passCode })
      .catch((err) => {
        this.error = (err as Error).message;
        throw err;
      })
      .finally(() => {
        this.pending = false;
      });
    },
    clearError() {
      this.error = '';
    },
    resetState() {
      Object.assign(this.$state, getInitialState());
    }
  }
});

export default useTotpStore;
